/**
 * The wallets that signed in to Morcast: data for the admin page, and what is new on it.
 *
 * "New" is counted for each admin in their own browser. The page remembers the first sign-in of
 * the newest wallet the admin has seen, and every wallet that first signed in after it is new.
 * Opening the page marks them seen. The list is read again every minute, so a new wallet shows in
 * the admin navigation without a reload.
 */

import { useQuery } from "@tanstack/react-query";
import { useSyncExternalStore } from "react";

import { api } from "../api/client.js";

/** A wallet, as `GET /api/admin/wallets` describes it. */
export interface AdminWallet {
  /** Wallet address in lowercase hex. */
  address: string;
  /** ISO 8601 times of the wallet's first and latest sign-in. */
  firstSignInAt: string;
  lastSignInAt: string;
  /** Whether it is one of the admin team's wallets. */
  isAdmin: boolean;
  /** Campaign requests it sent as a brand. */
  requests: number;
  /** Accounts it has linked as a creator. */
  accounts: number;
  /** Items it submitted to campaigns. */
  submissions: number;
}

/** The list the API answers with: the newest wallets, and how many signed in ever. */
export interface AdminWalletList {
  total: number;
  wallets: AdminWallet[];
}

/** Wallets the page lists at first, and at most. The API allows no more. */
export const WALLETS_LISTED = 200;
export const WALLETS_LISTED_AT_MOST = 500;

/** How often the list is read again while an admin page is open. */
const REFRESH_MS = 60_000;

/** The wallets that signed in, the newest first. */
export function useAdminWallets(limit: number = WALLETS_LISTED) {
  return useQuery({
    queryKey: ["admin", "wallets", limit],
    queryFn: () => api.get<AdminWalletList>(`/api/admin/wallets?limit=${limit}`),
    refetchInterval: REFRESH_MS,
  });
}

/** Where the browser keeps, for each admin, the newest first sign-in they have seen. */
const storageKey = (admin: string) => `morcast.admin.wallets-seen.${admin.toLowerCase()}`;

/** The hooks that read the stored time, told when this page changes it. */
const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  // Another tab of the same admin may mark the wallets seen.
  window.addEventListener("storage", listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", listener);
  };
}

function readSeen(admin: string | undefined): string | null {
  if (!admin) {
    return null;
  }
  try {
    return window.localStorage.getItem(storageKey(admin));
  } catch {
    // A browser that blocks storage shows every wallet as new, which hides nothing.
    return null;
  }
}

/**
 * Remembers that `admin` has seen every wallet that first signed in up to `until`.
 *
 * @param until ISO 8601 time of the newest first sign-in on the page.
 */
export function markWalletsSeen(admin: string, until: string): void {
  try {
    window.localStorage.setItem(storageKey(admin), until);
  } catch {
    // Without storage, the wallets stay new. Nothing else depends on it.
  }
  for (const listener of listeners) {
    listener();
  }
}

/** The first sign-in of the newest wallet `admin` has seen. Null before their first visit. */
export function useWalletsSeenUntil(admin: string | undefined): string | null {
  return useSyncExternalStore(subscribe, () => readSeen(admin));
}

/** Whether a wallet first signed in after the time the admin has seen wallets up to. */
export function isNewWallet(wallet: AdminWallet, seenUntil: string | null): boolean {
  return seenUntil === null || Date.parse(wallet.firstSignInAt) > Date.parse(seenUntil);
}

/** What a wallet does on Morcast, from what it has done so far. */
export function walletRoles(wallet: AdminWallet): ("Admin" | "Brand" | "Creator")[] {
  return [
    ...(wallet.isAdmin ? (["Admin"] as const) : []),
    ...(wallet.requests > 0 ? (["Brand"] as const) : []),
    ...(wallet.accounts > 0 || wallet.submissions > 0 ? (["Creator"] as const) : []),
  ];
}
